import { useCallback, useEffect, useState } from 'react';
import promoCodeService from '../services/promoCodeService';
import { useDialog } from '../context';
import { Btn, Field, Help, Inner, RemoveX, Row, SegPills, SubPanel, TextInput, usd } from './host/kit';

/**
 * Promo codes manager for the host and co-hosts with edit access. Lists existing codes
 * with usage counts and lets editors add or delete codes inline (right next to ticket
 * pricing on the event create/edit form, and again on the event-operations dashboard).
 *
 * Presentation: the PROMO CODES sub-panel (`id="promo"`, the MONEY tab's `+ ADD CODE`
 * jumps here) of the host event handoff (docs/plans/2026-09-host-event-edit/handoff/
 * README.md, step 3), built from ./host/kit and expected to render inside a `.hev` scope.
 *
 * Props:
 *   - eventId           — required for loading/creating codes. When null/undefined
 *                          (create mode before the draft has auto-saved), the section
 *                          renders an explanatory empty state and disables the form.
 *   - ticketPriceCents  — used to render a "$X per ticket after this code" preview.
 *   - className         — optional override for the wrapper.
 *   - canManage         — false for read-only co-hosts: codes are listed, nothing edits.
 */
export default function PromoCodesSection({ eventId, ticketPriceCents = 0, className = '', canManage = true }) {
  const { showAlert, showConfirm } = useDialog();

  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(!!eventId);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Form state
  const [code, setCode] = useState('');
  const [discountType, setDiscountType] = useState('percentage');
  const [discountValue, setDiscountValue] = useState('');
  const [maxUses, setMaxUses] = useState('');
  const [startsAt, setStartsAt] = useState('');
  const [expiresAt, setExpiresAt] = useState('');

  const refresh = useCallback(async () => {
    if (!eventId) {
      setCodes([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const r = await promoCodeService.listForEvent(eventId);
    if (r.success) setCodes(r.data || []);
    setLoading(false);
  }, [eventId]);

  useEffect(() => { refresh(); }, [refresh]);

  const resetForm = () => {
    setCode('');
    setDiscountType('percentage');
    setDiscountValue('');
    setMaxUses('');
    setStartsAt('');
    setExpiresAt('');
  };

  const handleCreate = async () => {
    if (!canManage || !eventId || !code.trim() || !discountValue || submitting) return;
    setSubmitting(true);
    const body = {
      code: code.trim(),
      discountType,
      discountValue:
        discountType === 'fixed'
          ? Math.round(parseFloat(discountValue) * 100)
          : parseInt(discountValue, 10),
    };
    if (maxUses) body.maxUses = parseInt(maxUses, 10);
    if (startsAt) body.startsAt = new Date(startsAt).toISOString();
    if (expiresAt) body.expiresAt = new Date(expiresAt).toISOString();

    const r = await promoCodeService.create(eventId, body);
    if (r.success && r.data) {
      // Optimistic prepend — no re-fetch round trip, no flash of loading state.
      setCodes((prev) => [r.data, ...prev]);
      resetForm();
      setShowForm(false);
    } else {
      await showAlert({ title: 'Could not create code', message: r.error || 'Please try again.', type: 'error' });
    }
    setSubmitting(false);
  };

  const handleDelete = async (id, codeLabel) => {
    if (!canManage) return;
    const ok = await showConfirm({
      title: 'DELETE PROMO CODE',
      message: `Delete ${codeLabel}? Existing uses are preserved; new redemptions will fail.`,
      confirmLabel: 'DELETE',
    });
    if (!ok) return;
    const r = await promoCodeService.remove(id);
    if (r.success) await refresh();
  };

  const formatDiscount = (c) =>
    c.discountType === 'percentage'
      ? `${c.discountValue}% OFF`
      : `${usd(c.discountValue / 100)} OFF`;

  const formatPreview = (c) => {
    if (!ticketPriceCents) return null;
    const discount =
      c.discountType === 'percentage'
        ? Math.floor((ticketPriceCents * Math.min(100, c.discountValue)) / 100)
        : Math.min(ticketPriceCents, c.discountValue);
    const after = Math.max(0, ticketPriceCents - discount);
    return `${usd(after / 100)} PER TICKET`;
  };

  // `OCT 3` — the short day for the STARTS / EXPIRES bits.
  const shortDay = (iso) => new Date(iso).toLocaleDateString('en-CA', { month: 'short', day: 'numeric' }).replace(/\./g, '').toUpperCase();

  // `20% OFF · 3/50 USED · $32.00 PER TICKET · STARTS OCT 1 · EXPIRES OCT 3`
  const metaLine = (c) => {
    const bits = [formatDiscount(c)];
    bits.push(c.maxUses != null ? `${c.timesUsed}/${c.maxUses} USED` : `${c.timesUsed} USED`);
    if (ticketPriceCents > 0) bits.push(formatPreview(c));
    if (c.startsAt) bits.push(`STARTS ${shortDay(c.startsAt)}`);
    if (c.expiresAt) bits.push(`EXPIRES ${shortDay(c.expiresAt)}`);
    return bits.join(' · ');
  };

  const isPercent = discountType === 'percentage';

  return (
    <SubPanel
      id="promo"
      title="PROMO CODES"
      sub="Add discount codes for sponsors, partners, or VIPs. Guests redeem at checkout."
      className={className}
      right={canManage ? (
        <Btn size="sm" onClick={() => setShowForm((v) => !v)} disabled={!eventId} style={{ flexShrink: 0 }}>
          {showForm ? 'CLOSE' : '+ ADD CODE'}
        </Btn>
      ) : undefined}
    >
      {!eventId && (
        <Help>Save the event basics first (title + date) — promo codes unlock once your draft is created.</Help>
      )}

      {!canManage && <Help>Only the host or a co-host with edit access can manage promo codes.</Help>}

      {eventId && loading && <Help>Loading…</Help>}

      {eventId && !loading && codes.map((c) => (
        <Row key={c.id}>
          <span style={{ fontSize: 13, fontWeight: 800, letterSpacing: '.08em', whiteSpace: 'nowrap' }}>{c.code}</span>
          <span style={{ flex: 1, minWidth: 0, fontSize: 9, letterSpacing: '.12em', color: '#6b7280', textTransform: 'uppercase', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {metaLine(c)}
          </span>
          {canManage && <RemoveX onClick={() => handleDelete(c.id, c.code)} label="Delete" />}
        </Row>
      ))}

      {eventId && canManage && showForm && (
        // Plain div, not a <form>: this section renders inside EventCreatePage's outer
        // <form> wrapper. Nested forms are invalid HTML — browsers strip the inner one
        // and the inner submit button ends up submitting the outer event form, which is
        // why "CREATE CODE" felt like it refreshed the page.
        <Inner grid>
          <Field label="CODE" small>
            <TextInput
              size="sm"
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              required
              maxLength={50}
              placeholder="TERRAVISTA20"
              style={{ textTransform: 'uppercase' }}
            />
          </Field>
          <Field label="TYPE" small as="div">
            <SegPills
              ariaLabel="Discount type"
              grow
              value={discountType}
              onChange={setDiscountType}
              options={[{ key: 'percentage', label: 'PERCENT' }, { key: 'fixed', label: 'FIXED $' }]}
            />
          </Field>
          <Field label={isPercent ? 'AMOUNT (%)' : 'AMOUNT ($)'} small>
            <TextInput
              size="sm"
              type="number"
              value={discountValue}
              onChange={(e) => setDiscountValue(e.target.value)}
              required
              min={isPercent ? 1 : 0.01}
              max={isPercent ? 100 : undefined}
              step={isPercent ? 1 : 0.01}
              placeholder={isPercent ? '20' : '5.00'}
            />
          </Field>
          <Field label="MAX USES (OPTIONAL)" small>
            <TextInput
              size="sm"
              type="number"
              value={maxUses}
              onChange={(e) => setMaxUses(e.target.value)}
              min={1}
              placeholder="Unlimited"
            />
          </Field>
          <Field label="STARTS (OPTIONAL)" small>
            <TextInput size="sm" type="datetime-local" value={startsAt} onChange={(e) => setStartsAt(e.target.value)} />
          </Field>
          <Field label="EXPIRES (OPTIONAL)" small>
            <TextInput size="sm" type="datetime-local" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} />
          </Field>
          <Btn className="hev-span2" full onClick={handleCreate} disabled={submitting}>
            {submitting ? 'SAVING…' : 'CREATE CODE'}
          </Btn>
        </Inner>
      )}

      {eventId && !loading && codes.length === 0 && !showForm && (
        <span className="hev-caps-muted">NO CODES YET.</span>
      )}
    </SubPanel>
  );
}
